import { AwaitContainer } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useExpertReportEvaluation } from '@/entities/expert-report';
import type { EvaluationBlock, EvaluationRuleResult } from '@/entities/expert-report';
import { useSettings } from '@/entities/settings';
import { ExpertReportEvaluationTable } from './ExpertReportEvaluationTable';

type Props = {
  conversationId: number;
  expertReportId: number;
};

export type FlatRow = {
  id: string;
  blockId: number;
  name: string;
  /** Балл, рассчитанный системой; null — блок оценён значением справочника */
  rating: number | null;
  maxRating: number | null;
  /** Балл, выставленный пользователем; null — балл не выставлялся */
  manualRating: number | null;
  /** Значение, полученное системой; null — блок оценивается баллами */
  value: string | null;
  /** Значение, выбранное пользователем; null — значение не выбиралось */
  manualValue: string | null;
  /** Справочник, из которого выбирается значение блока; null — блок оценивается баллами */
  possibleValues: string[] | null;
  /** Размерность балла; null — размерность не определена или результат не балл */
  unit: string | null;
  rules?: EvaluationRuleResult[];
  depth: number;
  isParent: boolean;
  isTotal: boolean;
};

/** Отбор не выставлен — показаны правила всех результатов. */
const NO_PICKED_RESULTS: string[] = [];

/** Оставляет правила с отобранными результатами; пустой отбор оставляет правила всех результатов. */
const pickRulesByResults = (rules: EvaluationRuleResult[] | undefined, pickedResults: string[]) =>
  pickedResults.length > 0 ? (rules?.filter((rule) => pickedResults.includes(rule.result)) ?? []) : rules;

/**
 * Разворачивает дерево блоков в плоские строки, оставляя правила с отобранными результатами
 * (пустой отбор — показаны правила всех результатов).
 *
 * Вложенный блок, в котором после отбора не осталось ни правил, ни показанных потомков,
 * в строки не попадает; верхнеуровневые блоки остаются при любом отборе.
 */
const flattenBlocks = (blocks: EvaluationBlock[], depth: number, pickedResults: string[]): FlatRow[] => {
  const rows: FlatRow[] = [];
  const isFiltered = pickedResults.length > 0;
  for (const block of blocks) {
    const isParent = block.children.length > 0;
    const childRows = isParent ? flattenBlocks(block.children, depth + 1, pickedResults) : [];
    const rules = isParent ? undefined : pickRulesByResults(block.rules, pickedResults);
    const isEmptyAfterFilter = childRows.length === 0 && !rules?.length;
    if (isFiltered && depth > 0 && isEmptyAfterFilter) continue;
    rows.push({
      id: `block-${block.blockId}`,
      blockId: block.blockId,
      name: block.blockName,
      rating: block.rating ?? null,
      maxRating: block.maxRating ?? null,
      manualRating: block.manualRating ?? null,
      value: block.value ?? null,
      manualValue: block.manualValue ?? null,
      possibleValues: block.possibleValues ?? null,
      unit: block.unit ?? null,
      rules,
      depth,
      isParent,
      isTotal: false,
    });
    rows.push(...childRows);
  }
  return rows;
};

export const ExpertReportTab: FC<Props> = ({ conversationId, expertReportId }) => {
  const { t } = useTranslation(['dialogs']);
  const { dialogsPage } = useSettings();
  // Отбор пуст — пользователь его не выставлял, показано всё заключение целиком
  const pickedResults = dialogsPage?.expertReportRuleResults ?? NO_PICKED_RESULTS;
  const isFiltered = pickedResults.length > 0;
  const { data, isLoading } = useExpertReportEvaluation(conversationId, expertReportId);
  const rows = useMemo<FlatRow[]>(() => {
    if (!data?.evaluation) return [];
    const { evaluation } = data;
    // Итог показывается в том виде, в каком его дало правило расчёта: значение справочника —
    // значением, балл — баллом с максимумом, а итог, в который не вошёл ни один балл, — прочерком
    const totalRow: FlatRow = {
      id: 'total',
      blockId: 0,
      name: t('expertReport.total'),
      rating: evaluation.totalRating ?? null,
      maxRating: evaluation.totalMaxRating ?? null,
      manualRating: null,
      value: evaluation.totalValue ?? null,
      manualValue: null,
      possibleValues: evaluation.totalPossibleValues ?? null,
      unit: evaluation.totalUnit ?? null,
      depth: 0,
      isParent: false,
      isTotal: true,
    };
    // Баллы берутся из оценки как есть: отбор меняет состав показанного, а не саму оценку
    return [totalRow, ...flattenBlocks(evaluation.blocks, 0, pickedResults)];
  }, [data, pickedResults, t]);

  if (isLoading) return <AwaitContainer isLoading />;
  return (
    <ExpertReportEvaluationTable
      rows={rows}
      expertReportId={expertReportId}
      conversationId={conversationId}
      isFiltered={isFiltered}
    />
  );
};
